/** Local knowledge base (EN) — help without LLM. */

export interface HelpArticle {
  id: string;
  title: string;
  keywords: string[];
  answer: string;
  relatedPath?: string;
  relatedLabel?: string;
}

export const SYSTEM_HELP: HelpArticle[] = [
  {
    id: 'today',
    title: 'O que é a tela Hoje?',
    keywords: ['today', 'hoje', 'needs you', 'queue', 'priority', 'what should', 'start'],
    answer:
      'Hoje mostra o que precisa da sua atenção: decisões, conversas urgentes, agenda, follow-ups, riscos e o que a IA já fez. Não cria aprovação artificial para o operacional autorizado. Atualize a qualquer momento.',
    relatedPath: '/central-inteligencia',
    relatedLabel: 'Abrir Hoje na Central',
  },
  {
    id: 'conversations',
    title: 'Como funcionam as Conversas?',
    keywords: ['conversations', 'chat', 'inbox', 'whatsapp', 'message', 'reply'],
    answer:
      'Conversas é o inbox do WhatsApp. Abra um contato, veja o status e use Resumir / Sugerir resposta. Atalho: ⌘K para pedir um contato pelo nome.',
    relatedPath: '/chat',
    relatedLabel: 'Abrir Conversas',
  },
  {
    id: 'register',
    title: 'Como funciona o Registrar?',
    keywords: ['register', 'voice', 'record', 'offline', 'call', 'meeting', 'mic', 'registrar', 'comando'],
    answer:
      'Registrar é a porta de acontecimentos do mundo real: ligações, reuniões, retornos combinados, handoffs e atualização de estágio. Toque no microfone ou use o Chat. Ações destrutivas (apagar, desligar agente/receptiva) ficam na Barra da IA (⌘K) com confirmação.',
    relatedPath: 'action:register-voice',
    relatedLabel: 'Abrir Registrar',
  },
  {
    id: 'approvals',
    title: 'Como funciona a fila de Aprovações?',
    keywords: ['approval', 'approvals', 'queue', 'ok', 'edit', 'undo', 'confirm', 'aprovacao'],
    answer:
      'Decisões sensíveis entram na fila. Ok executa; você pode editar o rascunho antes. Operacional já autorizado não precisa de aprovação artificial.',
    relatedPath: '/central-inteligencia?tab=aprovacoes',
    relatedLabel: 'Ver Aprovações',
  },
  {
    id: 'opportunities',
    title: 'What is Opportunities?',
    keywords: ['opportunities', 'funnel', 'radar', 'pipeline', 'deal', 'stage'],
    answer:
      'Revenue radar: contacts with opportunity signals inferred from chat (not a manual kanban). Each card shows why and a suggested next step.',
    relatedPath: '/central-inteligencia?tab=oportunidades',
    relatedLabel: 'Open Opportunities',
  },
  {
    id: 'central',
    title: 'Intelligence Center',
    keywords: ['central', 'briefing', 'intelligence', 'daily summary', 'what mends saw'],
    answer:
      'On-demand sector briefing with priorities, risks, opportunities, and recommendations. Prefer generating it from Mends AI chat — Markdown rendered for real.',
    relatedPath: '/central-inteligencia',
    relatedLabel: 'Open Intelligence Center',
  },
  {
    id: 'cmdk',
    title: 'What can I do with ⌘K?',
    keywords: ['cmd', 'command', 'palette', 'shortcut', 'ask', 'mends'],
    answer:
      '⌘K opens the command palette: Open Mends AI, Today, Approvals, Conversations, Register via voice, or type natural language to route into chat.',
    relatedPath: '/mente',
    relatedLabel: 'Open Mends AI',
  },
  {
    id: 'ai-actions',
    title: 'Does Mends AI only chat?',
    keywords: ['actions', 'execute', 'send', 'system', 'crm', 'do things'],
    answer:
      'No — it navigates, drafts/sends (one contact, after Ok), generates briefings, refreshes memory, and queues follow-ups. Always confirm sensitive work.',
    relatedPath: '/central-inteligencia?tab=aprovacoes',
    relatedLabel: 'Approvals',
  },
];

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function bestHelpAnswer(query: string): HelpArticle | null {
  const q = normalize(query);
  if (!q || q.length < 3) return null;

  let best: { article: HelpArticle; score: number } | null = null;
  for (const article of SYSTEM_HELP) {
    let score = 0;
    if (normalize(article.title).includes(q)) score += 8;
    for (const kw of article.keywords) {
      const k = normalize(kw);
      if (q.includes(k)) score += 5;
      else if (q.split(/\s+/).some((w) => w.length > 2 && (k.includes(w) || w.includes(k)))) score += 2;
    }
    if (/\b(how|what|help|como|o que)\b/.test(q) && score > 0) score += 2;
    if (!best || score > best.score) best = { article, score };
  }
  return best && best.score >= 5 ? best.article : null;
}
